import moment from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'components/Auth/AuthProvider';
import { toShortTimezone } from 'utils';

interface UseTimezoneDiffTextArgs {
    clientTimezone?: string | null;
}

interface UseTimezoneDiffTextResult {
    shouldShowTimezoneDiff: boolean;
    timezoneDiffText: string | null;
    providerTimezone: string | null;
    clientTimezoneAbbr: string | null;
    timezoneDiffHours: number;
    isClientAhead: boolean;
}

export const useTimezoneDiffText = ({ clientTimezone }: UseTimezoneDiffTextArgs): UseTimezoneDiffTextResult => {
    const { t, i18n } = useTranslation('criticalIncident', { useSuspense: false });
    moment.locale(i18n.language);
    const { effectiveProviderData } = useAuth();
    const [currentTime, setCurrentTime] = useState(() => moment());

    const { providerTimezone, clientTimezoneAbbr, timezoneDiffMinutes, shouldShowTimezoneDiff } = useMemo(() => {
        if (!clientTimezone) {
            return {
                providerTimezone: null,
                clientTimezoneAbbr: null,
                timezoneDiffMinutes: 0,
                shouldShowTimezoneDiff: false
            };
        }

        const providerTz = effectiveProviderData?.timeZone ? toShortTimezone(effectiveProviderData.timeZone) : null;
        const clientTz = toShortTimezone(clientTimezone);
        const tzDiff = moment().tz(clientTimezone).utcOffset() - moment().utcOffset();
        const shouldShow = clientTz !== providerTz && tzDiff !== 0;

        return {
            providerTimezone: providerTz,
            clientTimezoneAbbr: clientTz,
            timezoneDiffMinutes: tzDiff,
            shouldShowTimezoneDiff: shouldShow
        };
    }, [clientTimezone, effectiveProviderData?.timeZone, i18n.language]);

    useEffect(() => {
        if (!clientTimezone) return;

        let intervalId: ReturnType<typeof setInterval> | undefined;

        const updateTime = () => {
            setCurrentTime(moment());
        };

        const now = moment();
        const msUntilNextMinute = (60 - now.seconds()) * 1000 - now.milliseconds();

        const timeoutId = setTimeout(() => {
            updateTime();
            intervalId = setInterval(updateTime, 60000);
        }, msUntilNextMinute);

        return () => {
            clearTimeout(timeoutId);
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [clientTimezone, i18n.language]);

    const timezoneDiffHours = Math.abs(timezoneDiffMinutes / 60);
    const isClientAhead = timezoneDiffMinutes > 0;
    const timeFormat = useMemo(() => {
        if (i18n.language.startsWith('fr') || ['gb', 'ie'].includes(i18n.language?.toLowerCase().slice(-2))) {
            return 'HH:mm';
        }
        return 'h:mm A';
    }, [i18n.language]);

    const timezoneDiffText = useMemo(() => {
        if (!clientTimezone || !shouldShowTimezoneDiff) {
            return null;
        }

        return t('ci_timezone_banner_message', {
            currentTime: currentTime.tz(clientTimezone).format(timeFormat),
            clientTimezone: clientTimezoneAbbr,
            timezoneDiff: `${timezoneDiffHours} ${timezoneDiffHours === 1 ? t('hour', { count: 1 }) : t('hours', { count: timezoneDiffHours })}`,
            diffDir: isClientAhead ? t('ahead_of') : t('behind'),
            providerTimezone
        });
    }, [
        clientTimezone,
        shouldShowTimezoneDiff,
        t,
        currentTime,
        i18n.language,
        clientTimezoneAbbr,
        timezoneDiffHours,
        isClientAhead,
        providerTimezone
    ]);

    return {
        shouldShowTimezoneDiff,
        timezoneDiffText,
        providerTimezone,
        clientTimezoneAbbr,
        timezoneDiffHours,
        isClientAhead
    };
};